import { FC, useMemo, useState } from 'react';

import { ImageUploadBox } from '@service/seller/components/ImageUploadBox';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import { GiftItemListData } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import {
  createGiftStoreBanner,
  updateGiftStoreBanner,
} from '@shared/api/giftStoreBanner/giftStoreBanner.controller';
import {
  GiftStoreBannerCreate,
  GiftStoreBannerData,
  GiftStoreBannerLink,
  GiftStoreBannerLinkType,
} from '@shared/api/giftStoreBanner/giftStoreBanner.interface';
import { postImage } from '@shared/api/uploadImage';
import { LowerRole } from '@shared/types';
import { isGiftBrandLive } from '@shared/utils/giftPilot.util';
import {
  bugoBrandIdsWithGiftItems,
  bugoBrandsMissingBannerLink,
  fromKstDateTimeInput,
  refId,
  toKstDateTimeInput,
} from '@shared/utils/giftStoreBanner.util';

export const bugoBrandLabel = (brand: BugoBrand | undefined, fallbackId?: string) =>
  brand?.bugoAgency?.corpName || brand?._id || fallbackId || '-';

const LINK_TYPE_OPTIONS: { value: GiftStoreBannerLinkType; label: string; hint: string }[] =
  [
    {
      value: GiftStoreBannerLinkType.None,
      label: '없음',
      hint: '눌러도 아무 동작 안 함',
    },
    {
      value: GiftStoreBannerLinkType.GiftItemBrand,
      label: '브랜드 위치',
      hint: '그 브랜드 상품이 있는 곳으로 스크롤 + 카드 강조',
    },
    {
      value: GiftStoreBannerLinkType.DeliveryCategory,
      label: '분류 위치',
      hint: '배송 카테고리(하위분류) 구역으로 스크롤',
    },
    {
      value: GiftStoreBannerLinkType.GiftItemInfo,
      label: '상품 상세',
      hint: '그 상품 상세 페이지로 이동',
    },
  ];

type Props = {
  banner: GiftStoreBannerData | null; // null = 신규
  bugoBrands: BugoBrand[];
  giftItems: GiftItemListData;
  itemBrands: GiftItemBrand[];
  categories: GiftDeliveryCategoryData[];
  onClose: () => void;
  onSaved: () => void;
};

export const GiftStoreBannerFormModal: FC<Props> = function GiftStoreBannerFormModal({
  banner,
  bugoBrands,
  giftItems,
  itemBrands,
  categories,
  onClose,
  onSaved,
}) {
  const isEdit = !!banner;
  const existingImage =
    banner?.image && typeof banner.image === 'object' ? banner.image : null;

  const [title, setTitle] = useState(banner?.title ?? '');
  const [alt, setAlt] = useState(banner?.alt ?? '');
  const [isEnabled, setIsEnabled] = useState(banner?.isEnabled !== false);
  const [startAt, setStartAt] = useState(toKstDateTimeInput(banner?.startAt));
  const [endAt, setEndAt] = useState(toKstDateTimeInput(banner?.endAt));
  const [imageFile, setImageFile] = useState<File | null>(null);
  const initialTargets = (banner?.bugoBrands ?? [])
    .map((b) => refId(b))
    .filter((id): id is string => !!id);
  const [targetAll, setTargetAll] = useState(initialTargets.length === 0);
  const [targetIds, setTargetIds] = useState<string[]>(initialTargets);
  const [link, setLink] = useState<GiftStoreBannerLink>(
    banner?.link ?? { type: GiftStoreBannerLinkType.None },
  );
  const [isSaving, setIsSaving] = useState(false);

  // 답례품 스토어가 열리는 상조 = 판매상품이 한 건이라도 있는 상조
  const giftBugoBrands = useMemo(() => {
    const ids = bugoBrandIdsWithGiftItems(giftItems);
    return bugoBrands.filter((b) => ids.has(b._id));
  }, [bugoBrands, giftItems]);

  // 이미 지정돼 있는데 지금은 판매상품이 없는 상조도 목록에서 빠지지 않게
  const selectableBugoBrands = useMemo(() => {
    const shown = new Set(giftBugoBrands.map((b) => b._id));
    const extra = targetIds
      .filter((id) => !shown.has(id))
      .map((id) => bugoBrands.find((b) => b._id === id) ?? ({ _id: id } as BugoBrand));
    return [...giftBugoBrands, ...extra];
  }, [giftBugoBrands, targetIds, bugoBrands]);

  // 원상품 후보 = 어느 상조에서든 팔고 있는 원상품
  const itemInfoOptions = useMemo(() => {
    const map = new Map<string, string>();
    giftItems.forEach((item) => {
      const info = item.giftItemInfo;
      if (info?._id && info.isEnabled !== false && !map.has(info._id)) {
        map.set(info._id, info.name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'ko'),
    );
  }, [giftItems]);

  const selectedCategory = categories.find((c) => c._id === link.giftDeliveryCategory);

  const effectiveTargetIds = targetAll ? giftBugoBrands.map((b) => b._id) : targetIds;
  const missingIds = useMemo(
    () => bugoBrandsMissingBannerLink(link, effectiveTargetIds, giftItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [link, targetAll, targetIds, giftBugoBrands, giftItems],
  );
  const missingSet = new Set(missingIds);

  const setLinkType = (type: GiftStoreBannerLinkType) => setLink({ type });

  const toggleTarget = (id: string) =>
    setTargetIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleSave = async () => {
    if (isSaving) return;
    if (!title.trim()) return alert('관리용 이름을 입력해주세요.');
    if (!imageFile && !existingImage) return alert('배너 이미지를 올려주세요.');
    const startIso = startAt ? fromKstDateTimeInput(startAt) : null;
    const endIso = endAt ? fromKstDateTimeInput(endAt) : null;
    if (startIso && endIso && startIso >= endIso) {
      return alert('종료 시각이 시작 시각보다 뒤여야 합니다.');
    }
    if (!targetAll && targetIds.length === 0) {
      return alert('노출할 상조를 하나 이상 고르거나 "전체 상조"를 선택해주세요.');
    }
    if (link.type === GiftStoreBannerLinkType.GiftItemBrand && !link.giftItemBrand) {
      return alert('연결할 브랜드를 골라주세요.');
    }
    if (
      link.type === GiftStoreBannerLinkType.DeliveryCategory &&
      !link.giftDeliveryCategory
    ) {
      return alert('연결할 분류를 골라주세요.');
    }
    if (link.type === GiftStoreBannerLinkType.GiftItemInfo && !link.giftItemInfo) {
      return alert('연결할 상품을 골라주세요.');
    }
    if (
      missingIds.length > 0 &&
      !window.confirm(
        `연결 대상 상품이 없는 상조가 ${missingIds.length}곳 있습니다.\n그 상조 스토어에선 배너를 눌러도 이동하지 않습니다. 그대로 저장할까요?`,
      )
    ) {
      return;
    }

    try {
      setIsSaving(true);
      let imageId = existingImage?._id;
      if (imageFile) {
        const uploaded = await postImage(imageFile, LowerRole.Admin);
        imageId = uploaded._id;
      }
      const dto: GiftStoreBannerCreate = {
        title: title.trim(),
        image: imageId!,
        alt: alt.trim(),
        isEnabled,
        startAt: startIso,
        endAt: endIso,
        bugoBrands: targetAll ? [] : targetIds,
        link,
      };
      if (banner) await updateGiftStoreBanner(banner._id, dto);
      else await createGiftStoreBanner(dto);
      onSaved();
    } catch (error) {
      console.error(error);
      alert(isEdit ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none';
  const labelClass = 'mb-1 block text-sm font-medium text-gray-700';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={() => !isSaving && onClose()}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold">{isEdit ? '배너 수정' : '배너 추가'}</h2>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div>
            <label className={labelClass}>관리용 이름</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 떡보의하루 추석 배너"
              className={inputClass}
            />
          </div>

          <div>
            <ImageUploadBox
              title="배너 이미지"
              setFile={setImageFile}
              defaultImageUrl={existingImage?.url}
            />
            <p className="mt-2 text-xs text-gray-500">
              비율 720×348 (가로 약 2.07 : 1) 권장 · 다르면 위아래/좌우가 잘립니다.
            </p>
          </div>

          <div>
            <label className={labelClass}>대체 텍스트</label>
            <input
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="이미지 속 문구 (예: 정성으로 빚은 진심어린 답례품)"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>시작 (KST)</label>
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-400">비우면 즉시</p>
            </div>
            <div>
              <label className={labelClass}>종료 (KST)</label>
              <input
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-400">비우면 무기한</p>
            </div>
          </div>

          <label className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <div>
              <span className="text-sm font-medium text-gray-700">노출</span>
              <p className="text-xs text-gray-400">끄면 기간과 무관하게 내려갑니다.</p>
            </div>
            <input
              type="checkbox"
              aria-label="배너 노출"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              className="h-5 w-5"
            />
          </label>

          {/* 연결 */}
          <div>
            <label className={labelClass}>클릭 시</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {LINK_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setLinkType(opt.value)}
                  className={`rounded border px-3 py-2 text-sm ${
                    link.type === opt.value
                      ? 'border-indigo-600 bg-indigo-50 font-semibold text-indigo-700'
                      : 'border-gray-300 text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {LINK_TYPE_OPTIONS.find((o) => o.value === link.type)?.hint}
            </p>

            {link.type === GiftStoreBannerLinkType.GiftItemBrand && (
              <select
                aria-label="연결 브랜드"
                value={link.giftItemBrand ?? ''}
                onChange={(e) => setLink({ ...link, giftItemBrand: e.target.value || null })}
                className={`${inputClass} mt-2`}
              >
                <option value="">브랜드 선택</option>
                {itemBrands.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}

            {link.type === GiftStoreBannerLinkType.DeliveryCategory && (
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select
                  aria-label="연결 분류"
                  value={link.giftDeliveryCategory ?? ''}
                  onChange={(e) =>
                    setLink({
                      type: link.type,
                      giftDeliveryCategory: e.target.value || null,
                      subCategory: null,
                    })
                  }
                  className={inputClass}
                >
                  <option value="">카테고리 선택</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="연결 하위분류"
                  value={link.subCategory ?? ''}
                  onChange={(e) => setLink({ ...link, subCategory: e.target.value || null })}
                  disabled={!selectedCategory?.subCategories?.length}
                  className={`${inputClass} disabled:bg-gray-50`}
                >
                  <option value="">하위분류 전체</option>
                  {(selectedCategory?.subCategories ?? []).map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {link.type === GiftStoreBannerLinkType.GiftItemInfo && (
              <select
                aria-label="연결 상품"
                value={link.giftItemInfo ?? ''}
                onChange={(e) => setLink({ ...link, giftItemInfo: e.target.value || null })}
                className={`${inputClass} mt-2`}
              >
                <option value="">상품 선택</option>
                {itemInfoOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 대상 상조 */}
          <div>
            <label className={labelClass}>노출 상조</label>
            <div className="mb-2 flex gap-2">
              {[
                { all: true, label: `전체 상조 (${giftBugoBrands.length})` },
                { all: false, label: '선택한 상조만' },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setTargetAll(opt.all)}
                  className={`rounded border px-3 py-1.5 text-sm ${
                    targetAll === opt.all
                      ? 'border-indigo-600 bg-indigo-50 font-semibold text-indigo-700'
                      : 'border-gray-300 text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mb-2 text-xs text-gray-500">
              후보 = 판매상품이 등록된 상조 · 전체 상조는 나중에 새로 여는 상조에도 노출됩니다.
            </p>
            <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded border border-gray-200">
              {selectableBugoBrands.map((b) => {
                const checked = targetAll || targetIds.includes(b._id);
                const missing = missingSet.has(b._id);
                return (
                  <li key={b._id}>
                    <label
                      className={`flex items-center gap-2 px-3 py-2 text-sm ${
                        targetAll ? 'text-gray-500' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={targetAll}
                        onChange={() => toggleTarget(b._id)}
                      />
                      <span className="min-w-0 flex-1 truncate">{bugoBrandLabel(b)}</span>
                      <span
                        className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${
                          isGiftBrandLive(b)
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {isGiftBrandLive(b) ? '전체 실행' : '파일럿'}
                      </span>
                      {checked && missing && (
                        <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">
                          연결 대상 없음
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
            {missingIds.length > 0 && (
              <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                연결 대상 상품이 없는 상조 {missingIds.length}곳 — 그 스토어에선 배너를 눌러도
                이동하지 않습니다. 해당 상조를 빼거나 연결 대상을 바꿔주세요.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded border border-gray-300 px-4 py-2 text-sm"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {isSaving ? '저장 중…' : isEdit ? '수정' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
};
